<template>
  <div class="servers-groups-view">
    <!-- Loading states -->
    <LoadingOverlay :loading="isLoading" :message="loadingMessage" />
    <SkeletonLoader v-if="isInitialLoading" type="default" />

    <!-- Main content -->
    <div v-show="!isInitialLoading" class="content-wrapper">
      <!-- Header section -->
      <div class="page-header">
        <h1 class="page-title">{{ $t('broker.servers.title') }}</h1>
        <p class="page-description">{{ $t('broker.servers.description') }}</p>
      </div>

      <!-- Search section -->
      <div class="search-section">
        <v-text-field
          v-model="searchQuery"
          :placeholder="$t('broker.servers.searchPlaceholder')"
          outlined
          dense
          hide-details
          prepend-inner-icon="fa-regular fa-magnifying-glass"
          class="search-input"
          clearable
        />
      </div>

      <!-- Servers table -->
      <div class="table-container">
        <!-- Table header -->
        <div class="table-header">
          <div class="header-cell platform-col">{{ $t('broker.servers.table.platform') }}</div>
          <div class="header-cell server-col">{{ $t('broker.servers.table.server') }}</div>
          <div class="header-cell type-col">{{ $t('broker.servers.table.type') }}</div>
          <div class="header-cell groups-col">{{ $t('broker.servers.table.groupsAssociated') }}</div>
        </div>

        <!-- Table body -->
        <div class="table-body">
          <div v-for="server in filteredServers" :key="server.id" class="server-block">
            <!-- Server row -->
            <div
              class="server-row"
              :class="{ 'expanded': server.expanded }"
              @click="toggleServer(server)"
            >
              <div class="cell platform-col">
                <div class="platform-info">
                  <v-icon
                    class="expand-icon"
                    size="20"
                    :class="{ 'rotated': server.expanded }"
                  >
                    fa-regular fa-chevron-down
                  </v-icon>
                  <img
                    :src="getPlatformLogo(server.version)"
                    :alt="server.version"
                    class="platform-logo"
                  />
                  <span class="platform-name">{{ server.version }}</span>
                </div>
              </div>
              <div class="cell server-col">{{ server.host }}</div>
              <div class="cell type-col">
                <span
                  class="type-badge"
                  :class="server.type.toLowerCase()"
                >
                  {{ server.type }}
                </span>
              </div>
              <div class="cell groups-col">
                <span v-if="server.groupsLoaded">{{ server.groups.length }}</span>
                <span v-else>-</span>
              </div>
            </div>

            <!-- Groups section (expandable) -->
            <div
              v-if="server.expanded"
              class="groups-section"
            >
              <!-- Groups loading -->
              <div v-if="server.groupsLoading" class="groups-loading">
                <v-progress-circular indeterminate size="24" color="#28EA96" />
                <span>{{ $t('broker.servers.loadingGroups') }}</span>
              </div>

              <!-- No groups message -->
              <div v-else-if="server.groups.length === 0" class="no-groups">
                {{ $t('broker.servers.noGroups') }}
              </div>

              <!-- Groups table -->
              <div v-else class="groups-table">
                <div class="groups-header-label">
                  {{ $t('broker.servers.groupsCreatedLabel') }}
                </div>

                <!-- Groups header -->
                <div class="groups-header">
                  <div class="group-cell system-name-col">{{ $t('broker.servers.table.systemName') }}</div>
                  <div class="group-cell nickname-col">{{ $t('broker.servers.table.nickname') }}</div>
                  <div class="group-cell accounts-col">{{ $t('broker.servers.table.accounts') }}</div>
                  <div class="group-cell competitions-col">{{ $t('broker.servers.table.activeCompetitions') }}</div>
                </div>

                <!-- Divider -->
                <div class="groups-divider"></div>

                <!-- Group rows -->
                <div
                  v-for="group in server.groups"
                  :key="'group-' + group.id"
                  class="group-row"
                >
                  <div class="group-cell system-name-col">{{ group.groupName }}</div>
                  <div class="group-cell nickname-col">
                    <span class="nickname-text">{{ group.groupNickname || '-' }}</span>
                    <v-btn
                      icon
                      small
                      @click.stop="openEditModal(group)"
                      class="edit-btn"
                    >
                      <v-icon size="16">fa-regular fa-pen-to-square</v-icon>
                    </v-btn>
                  </div>
                  <div class="group-cell accounts-col">{{ group.activeAccountsCount ?? '-' }}</div>
                  <div class="group-cell competitions-col">{{ group.activeTournamentsCount ?? '-' }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Empty state -->
          <div v-if="filteredServers.length === 0 && !isInitialLoading" class="empty-state">
            <v-icon size="48" color="#ABABAB">fa-regular fa-server</v-icon>
            <p>{{ searchQuery ? $t('broker.servers.noResults') : $t('broker.servers.noServers') }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit Nickname Modal -->
    <EditNicknameModal
      :is-open="showEditModal"
      :group="selectedGroup"
      :loading="savingNickname"
      @close="closeEditModal"
      @save="handleSaveNickname"
    />

    <!-- Toast notification -->
    <v-snackbar
      v-model="showToast"
      :timeout="3000"
      color="#14161B"
      top
      right
    >
      <div class="toast-content">
        <v-icon color="#28EA96" class="mr-2">fa-regular fa-check-circle</v-icon>
        {{ toastMessage }}
      </div>
    </v-snackbar>
  </div>
</template>

<script>
import serversService from '../services/servers.service'
import LoadingOverlay from '../components/LoadingOverlay.vue'
import SkeletonLoader from '../components/SkeletonLoader.vue'
import EditNicknameModal from '../components/Servers/EditNicknameModal.vue'

export default {
  name: 'ServersGroupsView',
  components: {
    LoadingOverlay,
    SkeletonLoader,
    EditNicknameModal
  },
  data() {
    return {
      isLoading: false,
      isInitialLoading: true,
      loadingMessage: '',
      servers: [],
      searchQuery: '',
      showEditModal: false,
      selectedGroup: null,
      savingNickname: false,
      showToast: false,
      toastMessage: ''
    }
  },
  computed: {
    filteredServers() {
      if (!this.searchQuery) return this.servers

      const query = this.searchQuery.toLowerCase()
      return this.servers.filter(server =>
        server.name.toLowerCase().includes(query) ||
        server.version.toLowerCase().includes(query) ||
        server.type.toLowerCase().includes(query)
      )
    }
  },
  async mounted() {
    await this.loadServers()
  },
  methods: {
    async loadServers() {
      this.isLoading = true
      this.isInitialLoading = true
      this.loadingMessage = this.$t('broker.servers.loading')

      try {
        this.servers = await serversService.getServers()
      } catch (error) {
        console.error('Error loading servers:', error)
      } finally {
        setTimeout(() => {
          this.isLoading = false
          this.isInitialLoading = false
        }, 500)
      }
    },

    async toggleServer(server) {
      const isExpanding = !server.expanded

      // Collapse any other expanded server first
      if (isExpanding) {
        this.servers.forEach(s => {
          if (s.id !== server.id) {
            this.$set(s, 'expanded', false)
          }
        })
      }

      this.$set(server, 'expanded', isExpanding)

      if (server.expanded && !server.groupsLoaded) {
        this.$set(server, 'groupsLoading', true)

        try {
          const response = await serversService.getServerGroups(server.id)
          this.$set(server, 'groups', response.groups)
          this.$set(server, 'groupsLoaded', true)
        } catch (error) {
          console.error('Error loading groups:', error)
          this.$set(server, 'groups', [])
        } finally {
          this.$set(server, 'groupsLoading', false)
        }
      }
    },

    getPlatformLogo(version) {
      return serversService.getPlatformLogo(version)
    },

    openEditModal(group) {
      this.selectedGroup = group
      this.showEditModal = true
    },

    closeEditModal() {
      this.showEditModal = false
      this.selectedGroup = null
    },

    async handleSaveNickname({ groupId, nickname }) {
      this.savingNickname = true

      try {
        const updatedGroup = await serversService.updateGroupNickname(groupId, nickname)

        // Update the group in the local state
        for (const server of this.servers) {
          const group = server.groups.find(g => g.id === groupId)
          if (group) {
            group.groupNickname = updatedGroup.groupNickname
            break
          }
        }

        // Show success toast
        this.toastMessage = this.$t('broker.servers.nicknameUpdated')
        this.showToast = true

        this.closeEditModal()
      } catch (error) {
        console.error('Error saving nickname:', error)
      } finally {
        this.savingNickname = false
      }
    }
  }
}
</script>

<style scoped lang="scss">
.servers-groups-view {
  padding: 0;
  min-height: 100%;
}

.content-wrapper {
  padding: 0;
}

.page-header {
  margin-bottom: 20px;
}

.page-title {
  font-family: 'Aeonik', sans-serif;
  font-size: 30px;
  font-weight: 700;
  line-height: 40px;
  color: #3B3B3B;
  margin-bottom: 10px;
}

.page-description {
  font-family: 'Avenir LT Std', 'Avenir', sans-serif;
  font-size: 16px;
  font-weight: 350;
  line-height: 1.5;
  color: #3B3B3B;
  margin: 0;
}

.search-section {
  margin-bottom: 40px;
}

.search-input {
  max-width: 350px;

  ::v-deep .v-input__slot {
    min-height: 35px !important;
    background: transparent;
    border-radius: 4px;
  }

  ::v-deep fieldset {
    border-color: #ABABAB;
  }

  ::v-deep .v-input__prepend-inner {
    margin-top: 6px;

    .v-icon {
      color: #333;
      font-size: 16px;
    }
  }

  ::v-deep input {
    font-family: 'Avenir LT Std', 'Avenir', sans-serif;
    font-size: 16px;
    color: #ABABAB;

    &::placeholder {
      color: #ABABAB;
    }
  }
}

// Table styles
.table-container {
  border-radius: 5px;
  overflow: hidden;
}

.table-header {
  display: flex;
  background: #28EA96;
}

.header-cell {
  padding: 10px 16px;
  font-family: 'Avenir LT Std', 'Avenir', sans-serif;
  font-size: 16px;
  font-weight: 800;
  color: #3B3B3B;
  line-height: 1.5;
}

.platform-col {
  flex: 1;
  min-width: 200px;
}

.server-col {
  flex: 1;
  min-width: 200px;
}

.type-col {
  flex: 1;
  min-width: 120px;
  text-align: center;
}

.groups-col {
  flex: 1;
  min-width: 150px;
  text-align: center;
}

.table-body {
  background: #fff;
}

.server-row {
  display: flex;
  align-items: center;
  border-bottom: 1px solid #EBEBEB;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #FAFAFA;
  }

  &.expanded {
    background-color: #EBEBEB;
  }
}

.cell {
  padding: 16px;
  font-family: 'Avenir LT Std', 'Avenir', sans-serif;
  font-size: 16px;
  color: #2E3043;
  line-height: 1.5;
}

.platform-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.expand-icon {
  color: #333;
  transition: transform 0.2s ease;

  &.rotated {
    transform: rotate(180deg);
  }
}

.platform-logo {
  width: 36px;
  height: 36px;
  object-fit: contain;
  border-radius: 8px;
}

.platform-name {
  font-weight: 400;
}

.type-badge {
  display: inline-block;
  padding: 3px 12px;
  border-radius: 18px;
  font-family: 'Avenir LT Std', 'Avenir', sans-serif;
  font-size: 14px;
  font-weight: 800;

  &.live {
    background-color: #D7FFED;
    color: #005931;
  }

  &.demo {
    background-color: #E0F3FE;
    color: #045684;
  }
}

// Groups section
.groups-section {
  background: #EBEBEB;
  padding: 16px;
}

.groups-loading {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px;
  color: #666;
  font-size: 14px;
}

.no-groups {
  padding: 20px;
  color: #ABABAB;
  font-size: 14px;
  text-align: center;
}

.groups-header-label {
  font-family: 'Avenir LT Std', 'Avenir', sans-serif;
  font-size: 16px;
  font-weight: 800;
  color: #3B3B3B;
  padding: 0 16px;
  margin-bottom: 16px;
}

.groups-table {
  background: #EBEBEB;
}

.groups-header {
  display: flex;
}

.group-cell {
  padding: 10px 16px;
  font-family: 'Avenir LT Std', 'Avenir', sans-serif;
  font-size: 16px;
  color: #3B3B3B;
  line-height: 1.5;
}

.groups-header .group-cell {
  font-weight: 800;
}

.system-name-col {
  flex: 1;
}

.nickname-col {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 20px;
}

.accounts-col {
  flex: 1;
  text-align: center;
}

.competitions-col {
  flex: 1;
  text-align: center;
}

.groups-divider {
  height: 1px;
  background-color: #ABABAB;
  margin: 0 16px 0 16px;
}

.group-row {
  display: flex;
  align-items: center;

  .group-cell {
    padding: 10px 16px;
    font-weight: 400;
  }
}

.nickname-text {
  flex: 1;
}

.edit-btn.v-btn.v-btn--icon {
  background-color: transparent !important;

  .v-icon {
    color: #ABABAB !important;
  }

  &:hover {
    background-color: rgba(171, 171, 171, 0.1) !important;
  }
}

// Empty state
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #ABABAB;

  p {
    margin-top: 16px;
    font-size: 16px;
  }
}

// Toast
.toast-content {
  display: flex;
  align-items: center;
  font-family: 'Avenir LT Std', 'Avenir', sans-serif;
  font-size: 14px;
}
</style>
